import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'

interface RGB {
  r: number
  g: number
  b: number
}

interface HSL {
  h: number
  s: number
  l: number
}

function ColorPicker() {
  const { t } = useTranslation()
  const [hex, setHex] = useState('#137fec')
  const [rgb, setRgb] = useState<RGB>({ r: 19, g: 127, b: 236 })
  const [hsl, setHsl] = useState<HSL>({ h: 210, s: 87, l: 50 })

  const hexToRgb = (hex: string): RGB | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  const rgbToHex = (r: number, g: number, b: number): string => {
    return '#' + [r, g, b].map(x => {
      const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16)
      return hex.length === 1 ? '0' + hex : hex
    }).join('')
  }

  const rgbToHsl = (r: number, g: number, b: number): HSL => {
    r /= 255
    g /= 255
    b /= 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0, s = 0
    const l = (max + min) / 2

    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
        case g: h = ((b - r) / d + 2) / 6; break
        case b: h = ((r - g) / d + 4) / 6; break
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    }
  }

  const hslToRgb = (h: number, s: number, l: number): RGB => {
    h /= 360
    s /= 100
    l /= 100
    let r, g, b

    if (s === 0) {
      r = g = b = l
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1/6) return p + (q - p) * 6 * t
        if (t < 1/2) return q
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
        return p
      }
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s
      const p = 2 * l - q
      r = hue2rgb(p, q, h + 1/3)
      g = hue2rgb(p, q, h)
      b = hue2rgb(p, q, h - 1/3)
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    }
  }

  const updateFromHex = (newHex: string) => {
    setHex(newHex)
    const rgbVal = hexToRgb(newHex)
    if (rgbVal) {
      setRgb(rgbVal)
      setHsl(rgbToHsl(rgbVal.r, rgbVal.g, rgbVal.b))
    }
  }

  const updateFromRgb = (newRgb: RGB) => {
    setRgb(newRgb)
    setHex(rgbToHex(newRgb.r, newRgb.g, newRgb.b))
    setHsl(rgbToHsl(newRgb.r, newRgb.g, newRgb.b))
  }

  const updateFromHsl = (newHsl: HSL) => {
    setHsl(newHsl)
    const rgbVal = hslToRgb(newHsl.h, newHsl.s, newHsl.l)
    setRgb(rgbVal)
    setHex(rgbToHex(rgbVal.r, rgbVal.g, rgbVal.b))
  }

  return (
    <Layout showBackButton title={t('toolPages.colorPicker.title')}>
      <div className="flex flex-col p-4 gap-6">
        {/* Color Preview */}
        <div 
          className="h-32 rounded-xl shadow-inner relative overflow-hidden"
          style={{ backgroundColor: hex }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white/80 text-sm font-medium drop-shadow-md">
              {t('toolPages.colorPicker.clickToPick')}
            </span>
          </div>
          <input
            type="color"
            value={hex}
            onChange={(e) => updateFromHex(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        {/* HEX Input */}
        <div className="flex flex-col gap-2">
          <label className="section-label">HEX</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={hex}
              onChange={(e) => updateFromHex(e.target.value)}
              className="input font-mono flex-1"
              placeholder="#000000"
            />
            <CopyButton text={hex} />
          </div>
        </div>

        {/* RGB Inputs */}
        <div className="flex flex-col gap-2">
          <label className="section-label">RGB</label>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <span className="text-xs text-red-500 font-medium">R</span>
              <input
                type="number"
                min="0"
                max="255"
                value={rgb.r}
                onChange={(e) => updateFromRgb({ ...rgb, r: parseInt(e.target.value) || 0 })}
                className="input font-mono mt-1"
              />
            </div>
            <div>
              <span className="text-xs text-green-500 font-medium">G</span>
              <input
                type="number"
                min="0"
                max="255"
                value={rgb.g}
                onChange={(e) => updateFromRgb({ ...rgb, g: parseInt(e.target.value) || 0 })}
                className="input font-mono mt-1"
              />
            </div>
            <div>
              <span className="text-xs text-blue-500 font-medium">B</span>
              <input
                type="number"
                min="0"
                max="255"
                value={rgb.b}
                onChange={(e) => updateFromRgb({ ...rgb, b: parseInt(e.target.value) || 0 })}
                className="input font-mono mt-1"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`}
              readOnly
              className="input font-mono flex-1 bg-slate-50 dark:bg-[#0d131a]"
            />
            <CopyButton text={`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`} />
          </div>
        </div>

        {/* HSL Inputs */}
        <div className="flex flex-col gap-2">
          <label className="section-label">HSL</label>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <span className="text-xs text-purple-500 font-medium">H</span>
              <input
                type="number"
                min="0"
                max="360"
                value={hsl.h}
                onChange={(e) => updateFromHsl({ ...hsl, h: parseInt(e.target.value) || 0 })}
                className="input font-mono mt-1"
              />
            </div>
            <div>
              <span className="text-xs text-orange-500 font-medium">S</span>
              <input
                type="number"
                min="0"
                max="100"
                value={hsl.s}
                onChange={(e) => updateFromHsl({ ...hsl, s: parseInt(e.target.value) || 0 })}
                className="input font-mono mt-1"
              />
            </div>
            <div>
              <span className="text-xs text-cyan-500 font-medium">L</span>
              <input
                type="number"
                min="0"
                max="100"
                value={hsl.l}
                onChange={(e) => updateFromHsl({ ...hsl, l: parseInt(e.target.value) || 0 })}
                className="input font-mono mt-1"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={`hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`}
              readOnly
              className="input font-mono flex-1 bg-slate-50 dark:bg-[#0d131a]"
            />
            <CopyButton text={`hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`} />
          </div>
        </div>

        {/* Bottom spacing */}
        <div className="h-8"></div>
      </div>
    </Layout>
  )
}

export default ColorPicker
